import { observer } from "mobx-react";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import { Toolbar } from "primereact/toolbar";
import React, { useContext, useState } from "react";
import {
  DesktopClient,
  DeviceType,
} from "../../../api/firebase/types/device.types";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import ToastContext from "../../../components/toast/ToastContext";
import DeviceStore from "../../../store/DeviceStore";
import { DeviceToasts } from "../../../toastMessages/deviceToastMessages";
import DeviceSettingsHeader from "./DeviceSettingsHeader";
import DeviceSettingsForm from "./DeviceSettingsForm";
import SaveCancelFooter from "../../../components/footer/SaveCancelFooter";

export type DeviceDialogType = {
  device: DeviceType | DesktopClient;
};

const DeviceSettingsModal: React.FC<DeviceDialogType> = (props) => {
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);

  const showToast = useContext(ToastContext);

  const updateCurrentDevice = (updatedDevice: DeviceType | DesktopClient) => {
    setShowLoader(true);
    DeviceStore.updateCurrentDevice(updatedDevice)
      .then(() => {
        showToast(DeviceToasts.deviceSettingsSaved);
      })
      .catch(() => {
        showToast(DeviceToasts.deviceSettingsSaveError);
      })
      .finally(() => {
        DeviceStore.resetCurrentDevice();
        setShowLoader(false);
      });
  };

  const handleDeleteDeviceClicked = async () => {
    setShowLoader(true);
    const { deviceDeleted, deviceDeletionError } = DeviceToasts;
    const deletionSucceeded = await DeviceStore.deleteCurrentDevice();
    showToast(deletionSucceeded ? deviceDeleted : deviceDeletionError);
    setShowLoader(false);
  };

  const onCancel = () => {
    if (!props.device.scenarioIds?.length) setCancelDialogVisible(true);
    else DeviceStore.resetCurrentDevice();
  };

  return (
    <Dialog
      visible={!!props.device}
      header={
        <Toolbar
          left={() => <b>{props.device?.name}</b>}
          right={() => (
            <DeviceSettingsHeader
              currentDevice={props.device}
              setShowLoader={setShowLoader}
            />
          )}
        />
      }
      modal
      className="p-fluid device-detail p-col flex-auto"
      footer={
        <SaveCancelFooter formId="device-settings" cancelAction={onCancel} />
      }
      onHide={onCancel}
    >
      {showLoader && <ProgressBar mode="indeterminate" />}
      <DeviceSettingsForm
        currentDevice={props.device}
        onSave={updateCurrentDevice}
      />
      <ConfirmDialog
        header="Konfiguration abbrechen?"
        content="Ohne Konfiguration ist Ihre Meldeinstanz nicht einsatzbereit. Möchten Sie wirklich abbrechen?"
        isVisible={cancelDialogVisible}
        onHide={() => setCancelDialogVisible(false)}
        onClickYes={DeviceStore.resetCurrentDevice}
      />
      <ConfirmDialog
        header="Meldeinstanz entfernen?"
        content="Willst du die Meldeinstanz wirklich entfernen?"
        isVisible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onClickYes={handleDeleteDeviceClicked}
      />
    </Dialog>
  );
};

export default observer(DeviceSettingsModal);
